import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  EmptyState,
  failedEmptyState,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import { billingCheckoutPath } from "../../../routes/paths";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import BillingHistoryCard from "./BillingHistoryCard";
import PlanDurations from "./PlanDurations";
import PlanShowcase from "./PlanShowcase";
import PlansSkeleton from "./PlansSkeleton";
import SubscriptionStatusCard from "./SubscriptionStatusCard";
import TrialPlanCard from "./TrialPlanCard";
import TrialUpgradeCard from "./TrialUpgradeCard";
import { PRO_FEATURES, PRODUCT_NAME } from "../data/subscriptionPlans";
import { MONTHS_PER_CYCLE, monthsLabel } from "../pricing";
import { trialCountdown } from "../trialCountdown";
import { useBillingHistory } from "../hooks/useBillingHistory";
import { useCurrentSubscription } from "../hooks/useCurrentSubscription";
import { usePlans } from "../hooks/usePlans";
import { usePrepaidTerms } from "../hooks/usePrepaidTerms";
import {
  fetchInvoiceDocument,
  saveInvoicePdf,
} from "../services/subscriptionsService";
import type { BillingCycleId, SubscriptionPlan } from "../types/billing.types";
import type {
  Subscription,
  SubscriptionInvoice,
} from "../types/subscriptions.types";

type BillingView = "plans" | "details";

/** What a screen needs to call a plan by name. Less than a whole
 *  `SubscriptionPlan`, deliberately — see `planName`. */
interface PlanName {
  productName: string;
  cycleLabel: string;
}

const VIEWS: Array<{ id: BillingView; label: string }> = [
  { id: "plans", label: "Plans" },
  { id: "details", label: "Plan details" },
];

/**
 * Billing: what the workspace is paying for, or — for one that is not paying
 * yet — what it could.
 *
 * A section rather than a screen. It used to be `/billing`, opened from its own
 * rail item; it is the Settings screen's Billings tab now, which is why it
 * draws no page header and no page padding of its own — `SettingsPage` owns
 * both. `/billing` still redirects here, and the checkout under it is still a
 * screen: see `BILLING_SETTINGS_PATH`.
 *
 * ## Two views, and only for a workspace with a plan
 *
 * A workspace with nothing running sees the price list, full stop. There is
 * nothing else it could be shown: the Plan details screen is a subscription, its
 * invoices and the ways out of it, and a workspace with none of those has
 * nothing to administer. It used to be shown a made-up one — there was no
 * endpoint to draw a real one from — and that has gone with the endpoints
 * landing.
 *
 * A subscriber gets both, and opens on their own plan. The price list stays
 * within reach because moving from the month to the year is exactly what it is
 * still for; the cycle they are on is marked and cannot be bought again — see
 * `activeCycle`.
 *
 * So does a workspace on the free trial, which is the *other* way the product
 * is open and has its own pair of cards on the details view — see `trialing`,
 * `TrialPlanCard` and `TrialUpgradeCard`. It used to fall through to the price
 * list with the never-subscribed, which showed somebody with a fortnight of
 * complete access no sign of what they were on and no sign of how long they had
 * it. Its Upgrade button is the price list, so the switch is what a trial moves
 * between as much as a subscriber does.
 *
 * It is a pill group rather than the tab bar it was, because the tab bar above
 * it is the Settings screen's now and two of those in a column read as one
 * confused control.
 */
const BillingSection: React.FC = () => {
  const navigate = useNavigate();
  // The open view rides in the address beside the tab — see `showView`.
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const getToken = useTokenGetter();

  const { subscription, trial, accessSource } = useCurrentSubscription();
  const history = useBillingHistory();
  const priceList = usePlans();
  // The days actually paid for, which is the only record that says how long
  // the running term is — see `heldPlan`.
  const terms = usePrepaidTerms();
  // Which invoice's PDF is being fetched, so one row spins rather than the
  // whole table going busy for a download that belongs to one line of it.
  const [downloading, setDownloading] = useState<string | null>(null);

  const historyPhase = useLoadPhase(history.status === "loading");
  const plansPhase = useLoadPhase(priceList.status === "loading");

  /**
   * The plan the running term was actually bought on.
   *
   * `GET /billing/prepaid/terms/` names it by code — `PRO_3M` — and the price
   * list is what turns a code into a length, which is why the two are read
   * together. The same lookup `availabilityOf` makes.
   *
   * It is here because the subscription record cannot answer this. That record
   * is the mandate's, and its `plan.interval` is the *unit* a term is measured
   * in rather than how many of them were bought: a three-month term comes back
   * `MONTH`, which read as a cycle makes a quarter look like a month. It put
   * "Monthly" on the plan card and the Active badge on the wrong row of the
   * price list, for a practice whose invoice and end date were both correct.
   *
   * `null` where nothing is running, where the terms endpoint could not be
   * read, and where the term is on a plan the list has since stopped offering
   * — all three fall back to the record below, which is what this build did
   * everywhere before prepaid.
   */
  const heldPlan =
    (terms.current &&
      priceList.plans.find((plan) => plan.code === terms.current?.planCode)) ||
    null;

  /**
   * What to call the plan somebody is actually on.
   *
   * The term that is running comes first — see `heldPlan`. It is the only
   * record that says how many months were bought, and so the only one that can
   * name a quarter as a quarter.
   *
   * The mandate's record is next, because where there is no term to read it is
   * the only thing that knows what was bought. It carries the seller's own
   * name for the plan and the term it is billed on, both as the backend worded
   * them, so a subscription issued on a term the price list never offered — a
   * week of internal access, say — names itself correctly instead of being
   * drawn as the nearest card.
   *
   * The fetched price list is the fallback, and this app's own words are the
   * last one: the details screen is about a subscription somebody already has,
   * and their plan should not go unnamed, their invoices unreachable and their
   * cancel button unreachable with it, because a price list they are not even
   * being shown failed to load. The price list itself gets no such fallback: a
   * figure is not something to guess at. See `plansService`.
   */
  const planName = (subscription: Subscription | null): PlanName => {
    const listed = subscription
      ? priceList.plans.find((plan) => plan.id === subscription.cycle)
      : undefined;

    return {
      productName:
        heldPlan?.productName ??
        subscription?.planName ??
        listed?.productName ??
        PRODUCT_NAME,
      cycleLabel:
        heldPlan?.cycleLabel ??
        subscription?.cycleLabel ??
        listed?.cycleLabel ??
        monthsLabel(MONTHS_PER_CYCLE[subscription?.cycle ?? "monthly"]),
    };
  };

  /**
   * Whether this workspace is a subscriber rather than a shopper.
   *
   * Every state except the ones that are over — a plan whose payment has
   * failed still has a plan, and the screen that says so is the one with the
   * button that fixes it. `CREATED` counts too: a subscription made but never
   * authorised is a checkout somebody walked away from, and showing them their
   * own half-finished purchase is more use than showing them the price list
   * again.
   */
  /**
   * The cycle the workspace is actually running on, which is the one card that
   * says "Active" and the one that cannot be bought.
   *
   * Narrower than `subscribed` below, deliberately. That one asks which screen
   * to open and counts everything with a plan in it — an unauthorised checkout,
   * a refund on its way back — because all of those have something to
   * administer. This asks whether the plan is what is unlocking the product
   * right now, which is the server's `access_source` to say, and it is the
   * only claim a badge on the price list can honestly make: a `CREATED`
   * subscription has never been charged and a `HALTED` one has stopped, the
   * server answers neither with `SUBSCRIPTION`, and telling either that this
   * is their active plan while barring the button that would fix it is the
   * worst of both. A refund closes it the same way, however the record still
   * reads — and buying it again is exactly what the refunded card offers.
   */
  const activeCycle =
    heldPlan?.id ??
    (subscription && accessSource === "SUBSCRIPTION"
      ? subscription.cycle
      : null);

  const subscribed =
    !!subscription &&
    !["CANCELLED", "COMPLETED", "EXPIRED"].includes(subscription.status);

  /**
   * How much of the free trial is left, for a workspace on one.
   *
   * `null` for everybody else — a workspace that never took a trial, and one
   * whose trial the server calls finished. `active` is the backend's own flag
   * and not a comparison of dates against this machine's clock; see
   * `trialCountdown`.
   */
  const countdown = trialCountdown(trial);

  /**
   * Whether the details view is a trial rather than a subscription.
   *
   * A trial is the *other* way the product is open: during one the backend
   * answers `subscription: null` with `access_source: "FREE_TRIAL"`, so a screen
   * that only asked about the subscription showed somebody with fourteen days of
   * complete access the price list and nothing else — no sign of what they were
   * already on, and no sign of how long they had it for.
   *
   * `subscribed` wins where both are somehow true. Somebody who bought a plan
   * during their trial has a subscription to administer, and its invoices and
   * its way out are what this screen is for.
   */
  const trialing = !subscribed && !!countdown;

  /** Whether there is anything to put on the details view at all. */
  const hasPlan = subscribed || trialing;

  /**
   * The term the duration list has picked, held here rather than inside the
   * list.
   *
   * `null` until somebody chooses one, and the button under the list reads
   * "Select plan" and is off for exactly as long as it is. Nothing is picked
   * on the reader's behalf: the terms differ in what they commit somebody to
   * for a year, which is not a choice to make for them and then have them
   * press a button that already says "Buy".
   *
   * It lives on the screen rather than in the list so it survives a trip to
   * the Plan details view and back — a term chosen and then second-guessed is
   * still chosen on the way back.
   */
  const [chosen, setChosen] = useState<BillingCycleId | null>(null);

  /**
   * Which view the address bar asks for, where it asks for one.
   *
   * `?view=plans` is what the checkout's Edit comes back to, and what the
   * browser returns to when somebody walks back out of a checkout — see
   * `BILLING_PLANS_PATH`. It outranks the seeding below, which is the whole
   * point of it: arriving from a checkout is arriving to change the term, and
   * being shown the plan you already have is a click in the way of that.
   */
  const asked =
    searchParams.get("view") === "plans"
      ? "plans"
      : searchParams.get("view") === "details"
      ? "details"
      : null;

  const [view, setView] = useState<BillingView>(asked ?? "plans");
  // A subscriber lands on their own plan rather than on the price list — and so
  // does a workspace on its trial. Seeded during the render that first sees it
  // rather than in an effect, so the plans view is never painted and then
  // swapped out from under somebody — the same pattern as the checkout's form.
  //
  // Only where the address has not already answered the question: a URL naming
  // a view is somebody arriving at that view on purpose.
  const [seededView, setSeededView] = useState(false);
  if (hasPlan && !seededView && !asked) {
    setSeededView(true);
    setView("details");
  }

  /**
   * Moving between the two views, and saying so in the address.
   *
   * `replace` rather than a push, the same way the Settings tab bar above
   * records which tab is open: the back button is for leaving this screen, not
   * for walking back through every view that was glanced at on it.
   *
   * Written into the URL at all so that the entry a checkout is opened *from*
   * remembers which view it was — that entry is what `navigate(-1)` returns to
   * when Edit is pressed, and without this it would come back as the plan
   * somebody already has rather than the list they went to change it on.
   */
  const showView = (next: BillingView) => {
    setSeededView(true);
    setView(next);

    const params = new URLSearchParams(searchParams);
    params.set("view", next);
    setSearchParams(params, { replace: true });
  };

  // A workspace with no plan has nothing to put on the details view, so the
  // switch is not drawn and the price list is the whole screen.
  const showing: BillingView = hasPlan ? view : "plans";

  const invoices = history.invoices;

  /**
   * Fetching one invoice's PDF.
   *
   * The bytes come from the backend and are handed straight to the browser —
   * nothing is built here, because the document with the invoice number and
   * the registered details on it is the backend's, and a second one drawn in
   * the browser would be a different record of the same payment.
   */
  const handleDownload = async (invoice: SubscriptionInvoice) => {
    setDownloading(invoice.id);
    try {
      const pdf = await fetchInvoiceDocument(getToken, invoice.id);
      saveInvoicePdf(pdf, `naurist-${invoice.number}.pdf`);
    } catch (cause) {
      toast.error(
        "Could not download that invoice",
        cause instanceof Error ? cause.message : "Please try again."
      );
      console.error("invoice download failed:", cause);
    } finally {
      setDownloading(null);
    }
  };

  /**
   * Buying one: on to the checkout for that cycle.
   *
   * The plan travels in the URL rather than in router state, so the page it
   * opens can be reloaded, linked to and gone back to — see `billingCheckout`.
   * Which means this hands over nothing but the cycle, and the checkout looks
   * up the plan itself from the same list this screen drew.
   */
  const handleSubscribe = (plan: SubscriptionPlan) => {
    navigate(billingCheckoutPath(plan.id));
  };

  return (
    // Full height rather than as tall as its cards, so the card below — the
    // way out, or the trial's upgrade — can sit at the foot of the screen on a
    // page whose content does not reach it.
    //
    // `flex-1` and not `min-h-full`: this is a flex item of the Settings
    // screen's tab panel now, and a percentage min-height against a parent
    // that is itself sized by flex resolves to nothing — which left the bar
    // tucked under the history with the rest of the page empty below it. It
    // grows into the panel instead, and the panel takes its own height from
    // `SettingsPage` and in turn from `AppLayout`'s scroll region.
    <div className="flex flex-1 flex-col">
      {/* Only where there are two screens to move between — see the note on
          this component. Drawn as pills rather than tabs so it cannot be read
          as a second row of the Settings tab bar above it.

          Sized by its words at every width, and set against the left margin.
          It was stretched across the phone for a while, on the reasoning that
          a segmented control at the head of a screen is what a phone expects;
          the design says otherwise, and it is right. There are two views here,
          not a filter over what is below, and a control blown out to the full
          width of a screen reads as the heading of everything under it — which
          is exactly what it is not once Plan details is the open one. */}
      {hasPlan && (
        <div
          role="group"
          aria-label="Billing views"
          className="flex flex-shrink-0 items-center gap-1 self-start rounded-full border border-line bg-white p-1"
        >
          {VIEWS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={showing === item.id}
              onClick={() => showView(item.id)}
              className={`rounded-full px-3.5 py-1.5 text-[13px] 3xl:text-[14px] transition-colors max-md:px-4 max-md:py-2 max-md:text-[14px] ${
                showing === item.id
                  ? "bg-[#BFF4FB] font-medium text-[#007E8C]"
                  : "text-ink-muted hover:text-ink"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {showing === "details" && (subscription || countdown) ? (
        <div className="mt-5 3xl:mt-6 flex flex-1 flex-col gap-5 3xl:gap-6">
          {/* One card, two states. A trial first, because during one there is
              no term at all to draw the other from — see `TrialPlanCard`.

              Two where there were four. The money on its way back and the plan
              that ended because of it were the other two, and neither can
              happen now: a term that has been paid for is not refundable, so
              there is no refund for this screen to report the progress of. See
              `prepaidService`. */}
          {!subscription ? (
            countdown && (
              <TrialPlanCard
                productName={PRODUCT_NAME}
                countdown={countdown}
              />
            )
          ) : (
            <SubscriptionStatusCard
              subscription={subscription}
              productName={planName(subscription).productName}
              cycleLabel={planName(subscription).cycleLabel}
              // Re-signing a mandate is the checkout again, on the cycle
              // already being paid for: the plan has not changed, only the way
              // it is charged.
              onFixMandate={() =>
                // The term they are actually on, not the cadence the mandate
                // record names it by — see `heldPlan`.
                navigate(billingCheckoutPath(activeCycle ?? subscription.cycle))
              }
            />
          )}

          <BillingHistoryCard
            invoices={invoices}
            phase={historyPhase}
            error={history.error}
            onDownload={handleDownload}
            downloading={downloading}
            // A trial has bought nothing, so an empty table is the first-run
            // state rather than a first charge that has not settled yet — see
            // `purchased` on the card.
            purchased={!!subscription}
          />

          {/* Last on the screen, and only for a trial now.

              A card that ended a plan used to sit here for everybody else. It
              has gone with the thing it did: a term is bought outright and
              runs the months it covers, so there is no renewal to stop and
              nothing to cancel. What a subscriber does at the end of one is
              buy the next, which is the price list — and offering that at the
              foot of the screen they are already paying on would be pressing
              something nobody asked for. The term's own end date is on the
              card at the top, where it belongs.

              The trial card *is* pinned, and that is the difference: it is an
              offer rather than a way out, and it should not have to be
              scrolled to past a history.

              The tint is the page's own fill and is load-bearing: it is what
              the history passes behind rather than through. `pb` gives the
              card air above the window's edge while it is pinned, and the
              matching negative margin takes that space back out of the column
              so its resting position is exactly where it was. */}
          {!subscription ? (
            <div className="mt-auto sticky bottom-0 z-10 -mb-5 bg-[#FCFCFD] pb-5 pt-3 3xl:-mb-8 3xl:pb-8 3xl:pt-4">
              <TrialUpgradeCard
                productName={PRODUCT_NAME}
                // The price list, where the cycle is actually chosen — this
                // card names neither, and picking one on their behalf is not
                // the button's to do.
                onUpgrade={() => showView("plans")}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <section aria-labelledby="choose-your-plan" className="mt-5 3xl:mt-6">
          {/* A quiet line over the two panels on a wide screen, where the
              page is already titled and the panels say plainly enough what
              they are.

              On a phone it is the heading of the screen rather than a caption
              over it: the panels are stacked, so the only thing above the
              price list is this sentence, and set in grey at 15px it read as
              small print rather than as the question being asked. Same words,
              in the weight and the ink the rest of the app gives a heading. */}
          <h2
            id="choose-your-plan"
            className="text-[15px] 3xl:text-[17px] text-ink-muted max-md:text-[19px] max-md:font-semibold max-md:leading-snug max-md:text-[#00424B]"
          >
            {/* A workspace whose plan has run out is coming back rather than
                arriving, and the heading should not pretend it has never been
                here. */}
            {subscription
              ? "Start your plan again"
              : "Choose your plan & subscription duration"}
          </h2>

          {/* Said as soon as the request goes out, whether or not anything is
              drawn for it: the blocks that go up behind it are the shape of an
              answer rather than the answer. */}
          {priceList.status === "loading" && (
            <p role="status" className="sr-only">
              Loading the plans…
            </p>
          )}

          {priceList.status === "failed" ? (
            // No rows and no prices — and nothing invented in their place. A
            // price list is the one thing on this screen that must not be drawn
            // from anything but the answer the server gave.
            <EmptyState
              className="mt-2"
              {...failedEmptyState(
                "We could not load the plans",
                <Button size="sm" variant="secondary" onClick={priceList.reload}>
                  Try again
                </Button>
              )}
            />
          ) : (
            // What the product is on the left, how long to commit for on the
            // right. Side by side from `lg` up; stacked below it with the
            // panel first, because on a phone "what is this" is still the
            // question that comes before "for how long".
            //
            // Both columns share one height — the panel and the list of terms
            // beside it end on the same line, which is what the design asks
            // for and what stops the shorter of the two looking unfinished.
            //
            // Stretching is safe for the panel: its artwork is a `cover`
            // background, so a taller box crops it rather than distorting it.
            // And the button under the rows is pinned to the bottom rather
            // than left floating under the last one — see `PlanDurations`.
            <div className="mt-4 3xl:mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2 3xl:gap-6">
              {priceList.status === "ready" ? (
                <>
                  <PlanShowcase features={PRO_FEATURES} />
                  <PlanDurations
                    plans={priceList.plans}
                    value={chosen}
                    // The Billings tab offers no trial row, so the only choices
                    // this list can hand back are terms — see `DurationChoice`.
                    onChange={(choice) =>
                      choice !== "trial" && setChosen(choice)
                    }
                    onBuy={handleSubscribe}
                    activeCycle={activeCycle}
                    // A page of the app, so on a phone the way to finish
                    // belongs across the foot of the screen rather than
                    // trailing under the last row — see `PinnedCtaBar`.
                    pinCta
                  />
                </>
              ) : (
                <PlansSkeleton phase={plansPhase} />
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default BillingSection;
